import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  DnDidRange,
  MicrosoftDialPlanExceptionGroup,
  MicrosoftDialPlanRange,
  PrivilegedTenant,
  ZiroError,
} from '../../../../shared/models/generated/ziroModels';
import { BreadcrumbsService } from '../../../../shared/breadcrumbs/breadcrumbs.service';
import { ButtonStyles, ButtonTypes } from '../../../../button/button.component';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { ZiroModalService } from '../../../../shared/services/ziro-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable, of, Subscriber, Subscription, throwError } from 'rxjs';
import { BottomNavService, BottomNavUpdateButtonsList } from '../../../../shared/bottom-nav/bottom-nav.service';
import { ActivatedRoute, Router } from '@angular/router';
import { RangeFilterService } from '../../../services/range-filter.service';
import { catchError } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { TenantNavigationService } from '../../../services/tenant-navigation.service';
import { MsDialPlanExceptionGroupsContext } from '../../../contexts/ms-dial-plan-exception-groups.context';
import { DatatableBadge, DatatableColumn, DatatableRow } from '../../../../shared/tables/datatable/datatable.models';
import { TelephoneNumberFilter } from '../../../../shared/filters/telephone-number.filter';

export interface MicrosoftDialPlanExceptionGroupRow extends DatatableRow {
  name: string;
  type: string;
  exceptions: MicrosoftDialPlanRange[];
}

@Component({
  selector: 'ziro-microsoft-dial-plan-exception-management-list',
  templateUrl: './dial-plan-exception-management-list.component.html',
  styleUrls: ['../../../admin-page.scss'],
  providers: [RangeFilterService, TenantNavigationService],
  standalone: false,
})
export class MicrosoftDialPlanExceptionManagementListComponent implements OnInit, OnDestroy {
  private _msDialPlanExceptionGroupsContext = inject(MsDialPlanExceptionGroupsContext);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _ziroModalService = inject(ZiroModalService);
  private _translateService = inject(TranslateService);
  private _bottomNavService = inject(BottomNavService);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _rangeFilterService = inject(RangeFilterService);
  private _tenantNavigationService = inject(TenantNavigationService);
  private _telephoneNumberFilter = inject(TelephoneNumberFilter);

  ziroIcons = ZiroIcons;
  isLoading = true;
  aboutCardText = 'tkey;admin.ms_dial_plan_exception.list.about_card';
  dynamicNonTeamsGroupNames = new Set<string>();
  typeFilterOptions: string[] = ['Dynamic', 'Manual'];
  tenantInfo: PrivilegedTenant;
  data: MicrosoftDialPlanExceptionGroupRow[] = [];
  columns: DatatableColumn<MicrosoftDialPlanExceptionGroupRow>[] = [];
  tableAutomationId = 'microsoft-dial-plan-exception-management-table';

  private _tenantId: string;
  private _subscriptions = new Subscription();

  ngOnInit(): void {
    this._tenantId = this._route.snapshot.params.tenantId;

    const tenantSub = this._tenantNavigationService.getTenantInfo(this._route).subscribe((tenantInfo) => {
      this.tenantInfo = tenantInfo;
      this._updateBreadcrumbs();
    });

    this._subscriptions.add(tenantSub);

    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'ms-dial-plan-exception-list-add-group-button',
          label: 'tkey;admin.ms_dial_plan_exception.list.add_button.label',
          icon: ZiroIcons.ADD,
          buttonClass: ButtonStyles.PRIMARY,
          dataAutomation: 'ms-dial-plan-exception-list-add-group-button',
          type: ButtonTypes.LINK,
          href: window.location.href + '/dial-plan-exception-group',
        },
      ]),
    );

    const sub = combineLatest([
      this._msDialPlanExceptionGroupsContext.state$,
      this._msDialPlanExceptionGroupsContext.getDynamicNonTeamsDialPlanExceptionGroups(this._tenantId).pipe(
        catchError((err: HttpErrorResponse) => {
          return (err.error as ZiroError).reasonCode === 'MISSING_NUMBERS_API_SETTING'
            ? of(null)
            : throwError(() => err);
        }),
      ),
    ]).subscribe(([msDialPlanExceptionGroups, dynamicNonTeamsDialPlanExceptionGroupsMaybe]) => {
      const dynamicNonTeamsDialPlanExceptionGroups = dynamicNonTeamsDialPlanExceptionGroupsMaybe || [];
      this.columns = [
        {
          name: 'name',
          label: 'tkey;admin.ms_dial_plan_exception.group_name.label',
          columnSize: dynamicNonTeamsDialPlanExceptionGroups.length ? 'col-sm-3' : 'col-sm-4',
        },
        {
          name: 'type',
          label: 'tkey;admin.ms_dial_plan_exception.group_type.label',
          columnSize: 'col-sm-2',
          hideColumn: !dynamicNonTeamsDialPlanExceptionGroups.length,
        },
        {
          name: 'exceptions',
          label: 'tkey;admin.ms_dial_plan_exception.exceptions.label',
          columnSize: dynamicNonTeamsDialPlanExceptionGroups.length ? 'col-sm-6' : 'col-sm-7',
          sortFn: (a: DnDidRange[], b: DnDidRange[]) => {
            const aRange: DnDidRange = a[0] || { start: '0', end: '' };
            const bRange: DnDidRange = b[0] || { start: '0', end: '' };
            return parseInt(aRange.start) - parseInt(bRange.start);
          },
        },
      ];
      if (dynamicNonTeamsDialPlanExceptionGroups.length) {
        this.aboutCardText = 'tkey;admin.ms_dial_plan_exception.list.about_card.dynamic_non_teams_groups_exist';
        dynamicNonTeamsDialPlanExceptionGroups.forEach((group) => this.dynamicNonTeamsGroupNames.add(group.name));
      }
      this._buildTableRows([...msDialPlanExceptionGroups, ...dynamicNonTeamsDialPlanExceptionGroups]);
      this.isLoading = false;
    });
    this._subscriptions.add(sub);
  }

  ngOnDestroy(): void {
    this._breadcrumbsService.clearBreadcrumbs();
    this._subscriptions.unsubscribe();
  }

  isTypeMatch(filters: string[], row: MicrosoftDialPlanExceptionGroupRow): boolean {
    return !filters.length || filters.includes(row.type);
  }

  private _onDeleteClicked(group: MicrosoftDialPlanExceptionGroup): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.DELETE_SMALL,
        iconClass: 'text-danger',
        title: this._translateService.instant('tkey;admin.msdialplan.management.delete_modal.title'),
        promptBody: this._translateService.instant('tkey;admin.ms_dial_plan_exception.delete.message', {
          name: group.name,
        }),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => this._deleteGroup(group),
          },
        ],
      },
    };
    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _buildTableRows(exceptionGroups: MicrosoftDialPlanExceptionGroup[]): void {
    this.data = exceptionGroups.map((group) => {
      const isDynamicGroup = this.dynamicNonTeamsGroupNames.has(group.name);
      return {
        name: group.name,
        type: isDynamicGroup ? 'Dynamic' : 'Manual',
        exceptions: group.exceptionRanges,
        hardcodedTooltips: {
          name: isDynamicGroup ? 'tkey;admin.ms_dial_plan_exception.dynamic.group_name.tooltip' : '',
        },
        badges: {
          exceptions: {
            values: group.exceptionRanges.map(
              (range: DnDidRange): DatatableBadge<MicrosoftDialPlanExceptionGroupRow> =>
                range.start === range.end
                  ? { label: this._telephoneNumberFilter.transform(range.start), cssClass: 'bg-secondary' }
                  : { label: this._rangeFilterService.getE164FormattedRange(range), cssClass: 'bg-secondary' },
            ),
          },
        },
        html: {
          type: `
                <span class="alert alert-inline alert-info text-capitalize">
                  <i class="${isDynamicGroup ? this.ziroIcons.FIX_IT : this.ziroIcons.HAND} me-1"></i>
                    ${isDynamicGroup ? 'Dynamic' : 'Manual'}
                </span>
          `,
        },
        actions: [
          {
            type: ButtonTypes.LINK,
            style: ButtonStyles.OUTLINE_PRIMARY,
            href: window.location.href + `/dial-plan-exception-group/${group.id}`,
            dataAutomation: 'ms-dial-plan-exception-group-edit',
            icon: ZiroIcons.EDIT,
            disabled: isDynamicGroup,
            disabledTooltip: isDynamicGroup ? 'tkey;admin.ms_dial_plan_exception.dynamic.button.tooltip' : '',
            isTooltipDisabled: !isDynamicGroup,
            onClick: () => this._router.navigate(['dial-plan-exception-group', group.id], { relativeTo: this._route }),
          },
          {
            type: ButtonTypes.BUTTON,
            style: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'ms-dial-plan-exception-group-delete',
            icon: ZiroIcons.DELETE_SMALL,
            disabled: isDynamicGroup,
            disabledTooltip: isDynamicGroup ? 'tkey;admin.ms_dial_plan_exception.dynamic.button.tooltip' : '',
            isTooltipDisabled: !isDynamicGroup,
            onClick: () => this._onDeleteClicked(group),
          },
        ],
      };
    });
  }

  private _deleteGroup(group: MicrosoftDialPlanExceptionGroup): Observable<void> {
    return new Observable((subscriber: Subscriber<void>) => {
      this._msDialPlanExceptionGroupsContext.delete(Number(group.id), group.name).subscribe(() => {
        subscriber.next(null);
        subscriber.complete();
      });
    });
  }

  private _updateBreadcrumbs(): void {
    this._tenantNavigationService.buildTenantBreadcrumbs(this.tenantInfo, [
      { label: 'tkey;admin.ms_dial_plan_exception.title' },
    ]);
  }
}
